import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useNavigate } from 'react-router-dom';
import { Camera, QrCode, AlertTriangle, CheckCircle } from 'lucide-react';
import TopBar from '../components/TopBar';
import Button from '../components/Button';

type ScannerState = 'idle' | 'scanning' | 'processing' | 'success' | 'error';
type CameraPermission = 'prompt' | 'granted' | 'denied';

export default function QRScannerPage() {
  const navigate = useNavigate();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [state, setState] = useState<ScannerState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [cameraPermission, setCameraPermission] = useState<CameraPermission>('prompt');

  useEffect(() => {
    const initScanner = async () => {
      try {
        // Check camera permission
        const permission = await navigator.permissions.query({ name: 'camera' as PermissionName });
        setCameraPermission(permission.state as CameraPermission);

        permission.addEventListener('change', () => {
          setCameraPermission(permission.state as CameraPermission);
        });
      } catch {
        console.log('Permission API not supported, will request on scan');
      }
    };

    initScanner();
  }, []);

  const startScanning = async () => {
    setError(null);
    setState('scanning');

    try {
      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' }, // Use back camera on mobile
        {
          fps: 10, // Frames per second
          qrbox: { width: Math.min(250, window.innerWidth - 80), height: Math.min(250, window.innerWidth - 80) }, // Responsive scanning box
        },
        (decodedText) => {
          // Successfully scanned QR code
          handleScan(decodedText);
        },
        () => {
          // Scanning errors (ignore most of these as they happen frequently)
        }
      );

      setCameraPermission('granted');
    } catch (err) {
      console.error('Failed to start scanner:', err);
      setError('Failed to access camera. Please check permissions.');
      setState('error');
      setCameraPermission('denied');
    }
  };

  const stopScanning = async () => {
    if (scannerRef.current && state === 'scanning') {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setState('idle');
  };

  const handleScan = async (data: string) => {
    // Set processing state
    setState('processing');
    
    // Stop scanning immediately
    await stopScanning();

    try {
      // Parse the URL to extract event ID
      const url = new URL(data);
      
      // Check if it's a valid check-in URL
      if (url.pathname.includes('/event/') && url.pathname.includes('/check-in')) {
        // Extract event ID from path: /event/:id/check-in
        const pathParts = url.pathname.split('/');
        const eventIndex = pathParts.indexOf('event');
        const eventId = pathParts[eventIndex + 1];

        if (eventId) {
          setState('success');
          
          // Small delay to show success state
          setTimeout(() => {
            navigate(`/event/${eventId}/check-in?source=qr`);
          }, 800);
          return;
        }
      }

      // If we get here, the QR code wasn't valid
      setError('Invalid QR code. Please scan an event check-in QR code.');
      setState('error');
    } catch {
      // Not a valid URL or parsing failed
      setError('Invalid QR code format. Please scan an event check-in QR code.');
      setState('error');
    }
  };

  const resetScanner = () => {
    setError(null);
    setState('idle');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current && state === 'scanning') {
        scannerRef.current.stop().catch(console.error);
      }
    };
  }, [state]);

  return (
    <div className="min-h-screen bg-oro-bg">
      <TopBar title="Scan QR Code" onBack={() => window.history.back()} />
      
      <div className="safe-area-top pt-4 px-4 pb-20">
        {/* Scanner Container */}
        <div className="bg-oro-surface border border-oro-border rounded-2xl overflow-hidden mb-6">
          <div className="relative">
            <div 
              id="qr-reader" 
              className={`w-full min-h-[300px] ${state === 'scanning' ? '' : 'hidden'}`}
            />
            
            {/* Idle State */}
            {state === 'idle' && (
              <div className="p-8 text-center">
                <div className="bg-oro-surface-alt rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <QrCode className="w-8 h-8 text-text-muted" />
                </div>
                <h2 className="font-semibold text-text-primary mb-2">
                  Ready to Scan
                </h2>
                <p className="text-text-secondary text-sm">
                  Point your camera at an event QR code
                </p>
              </div>
            )}

            {/* Processing State */}
            {state === 'processing' && (
              <div className="p-8 text-center">
                <div className="animate-pulse bg-oro-surface-alt rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <QrCode className="w-8 h-8 text-text-muted" />
                </div>
                <h2 className="font-semibold text-text-primary mb-2">
                  Processing QR Code...
                </h2>
                <p className="text-text-secondary text-sm">
                  Please wait a moment
                </p>
              </div>
            )}

            {/* Success State */}
            {state === 'success' && (
              <div className="p-8 text-center">
                <div className="bg-green-500/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="font-semibold text-green-400 mb-2">
                  QR Code Found!
                </h2>
                <p className="text-text-secondary text-sm">
                  Redirecting to check-in...
                </p>
              </div>
            )}

            {/* Error State */}
            {state === 'error' && (
              <div className="p-8 text-center">
                <div className="bg-red-500/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                </div>
                <h2 className="font-semibold text-red-400 mb-2">
                  Scan Failed
                </h2>
                {error && (
                  <p className="text-text-secondary text-sm mb-4">
                    {error}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Camera Permission Warning */}
        {cameraPermission === 'denied' && state !== 'scanning' && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-yellow-400 mb-1">
                  Camera Access Required
                </h3>
                <p className="text-yellow-400/80 text-sm">
                  Please enable camera permissions in your browser settings to scan QR codes.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
          <h3 className="font-medium text-text-primary mb-3">How to Scan</h3>
          <div className="space-y-2 text-sm text-text-secondary">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-oro-surface-alt rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-accent">1</span>
              </div>
              <span>Tap "Start Scanning" below</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-oro-surface-alt rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-accent">2</span>
              </div>
              <span>Point your camera at the QR code</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-oro-surface-alt rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-accent">3</span>
              </div>
              <span>Wait for automatic detection</span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-3">
          {state === 'idle' || state === 'error' ? (
            <Button
              onClick={state === 'error' ? resetScanner : startScanning}
              variant="primary"
              size="lg"
              className="w-full"
              disabled={cameraPermission === 'denied'}
            >
              <Camera className="w-5 h-5 mr-2" />
              {state === 'error' ? 'Try Again' : 'Start Scanning'}
            </Button>
          ) : state === 'scanning' ? (
            <Button
              onClick={stopScanning}
              variant="secondary"
              size="lg"
              className="w-full"
            >
              Stop Scanning
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
